import * as amplitude from "@amplitude/analytics-browser";
import { Button, Flex, TabList, TabPanel, TabPanels } from "@chakra-ui/react";
import React from "react";
import { RiDownloadLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { AdminPage } from "src/components/Layout/AdminPage";
import { Tab, TabsWithHash } from "src/components/Layout/TabsWithHash";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { SearchAndFilterTypes } from "src/constants";
import { exportCapacitiesByEnrollmentPeriod } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import * as breadcrumb from "src/services/breadcrumb";
import { csvExport } from "src/services/dataTransfer";
import { HasuraRole } from "src/types/hasuraRole";
import { Capacities as CapacitiesV2 } from "./Capacities";
import { History, SelectedTransaction } from "./History";
import { HistoryDetail } from "./HistoryDetail";
import { useImportCapacities } from "./ImportCapacities";

const UPDATE_TAB_HASH = "update";

export const Capacities = () => {
  const organization = useOrganization();
  const canViewHistory = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);

  const [searchParams] = useSearchParams();
  const enrollmentPeriodId =
    searchParams.get(SearchAndFilterTypes.EnrollmentPeriod) ?? "";
  const avelaToast = useAvelaToast();

  const { importState, importButton, importPage } = useImportCapacities({
    enrollmentPeriodId,
  });

  const [selectedTransaction, setSelectedTransaction] =
    React.useState<SelectedTransaction | null>(null);

  const onSelectTransaction = React.useCallback((item: SelectedTransaction) => {
    setSelectedTransaction(item);
    window.location.hash = UPDATE_TAB_HASH;
  }, []);

  // Clear selectedTransaction when the user navigates away from the update tab.
  React.useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash !== UPDATE_TAB_HASH) {
        setSelectedTransaction(null);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  if (importState.state !== "idle") {
    return importPage;
  }

  return (
    <AdminPage
      breadcrumb={
        <Breadcrumb
          items={breadcrumb.capacities.getBreadcrumbsForList(organization)}
        />
      }
      heading={
        <AdminHeading
          title={"Capacities"}
          actionButton={
            <Flex gap="2">
              {importButton}
              <Button
                leftIcon={<RiDownloadLine />}
                variant="solid"
                onClick={async () => {
                  amplitude.track("Clicked export all");
                  const loadingToastId = avelaToast({
                    title: `Export in progress`,
                    description: `Download will start shortly`,
                    status: "loading",
                  });
                  try {
                    const records = await exportCapacitiesByEnrollmentPeriod({
                      path: {
                        enrollmentPeriodId,
                      },
                    });
                    if (records.data?.data) {
                      csvExport(records.data?.data, "capacities.csv");
                    } else {
                      console.error("Empty export data");
                      avelaToast.genericError();
                    }
                  } catch (error) {
                    console.error(error);
                    avelaToast.genericError();
                  } finally {
                    avelaToast.close(loadingToastId);
                  }
                }}
              >
                Export all
              </Button>
            </Flex>
          }
        />
      }
    >
      <TabsWithHash variant="admin" isLazy>
        <TabList>
          <Tab
            hashKey="capacities"
            onClick={() => {
              amplitude.track("Viewed capacities");
            }}
          >
            Capacities
          </Tab>
          {canViewHistory ? (
            <Tab
              hashKey="History"
              onClick={() => {
                amplitude.track("Viewed history");
              }}
            >
              History
            </Tab>
          ) : null}
          {canViewHistory ? (
            <Tab
              hashKey={UPDATE_TAB_HASH}
              display={selectedTransaction ? undefined : "none"}
              onClick={() => {
                amplitude.track("Viewed history detail");
              }}
            >
              {selectedTransaction
                ? `Updates from ${selectedTransaction.timestamp} by ${selectedTransaction.userRole} ${selectedTransaction.userFullName}`
                : ""}
            </Tab>
          ) : null}
        </TabList>
        <TabPanels>
          <TabPanel>
            <CapacitiesV2 enrollmentPeriodId={enrollmentPeriodId} />
          </TabPanel>
          {canViewHistory ? (
            <TabPanel>
              <History
                enrollmentPeriodId={enrollmentPeriodId}
                onSelectTransaction={onSelectTransaction}
              />
            </TabPanel>
          ) : null}
          {canViewHistory ? (
            <TabPanel>
              {selectedTransaction && (
                <HistoryDetail
                  enrollmentPeriodId={enrollmentPeriodId}
                  transactionId={selectedTransaction.transactionId}
                />
              )}
            </TabPanel>
          ) : null}
        </TabPanels>
      </TabsWithHash>
    </AdminPage>
  );
};
